import axios from 'axios';
import { resolveUseMock } from './opsBriefingMockMode';
import {
    OpsBriefingAccessResponse,
    OpsBriefingResponse,
    EvidenceResponse,
    MembersResponse,
    CoachesResponse,
    HistoryResponse,
    OrderDto,
    CreateOrderRequest,
    UpdateOrderRequest,
    UpdateBriefingItemStatusRequest,
    UpdateBriefingItemStatusResponse,
    UpdateMemberVisibilityResponse,
    UpdateCoachVisibilityResponse,
    RetractOrderRequest,
    DateKst,
    CoachOnboardingBriefing,
} from '../types/opsBriefing';
import { FIXTURE_BRIEFING } from '../scenes/OpsBriefing/coachOnboardingFixture';
import {
    mockOpsBriefing,
    mockEvidence,
    mockMembers,
    mockCoaches,
    mockHistory,
    mockOrderList,
    mockCreateOrder,
    mockUpdateOrder,
    mockRetractOrder,
    mockSetHistoryItemDone,
    mockSetBriefingItemStatus,
    mockSetMemberHidden,
    mockSetCoachHidden,
} from '../scenes/OpsBriefing/mockApi';
import { OnboardingPayload } from '../scenes/OpsBriefing/onboarding';
import { CoachSettlingPayload } from '../scenes/OpsBriefing/coachSettling';
import { mockOnboarding } from '../scenes/OpsBriefing/onboardingMock';
import { mockCoachSettling, mockUnlockContext } from '../scenes/OpsBriefing/coachSettlingMock';

/**
 * 데일리 운영 브리핑 API.
 *
 * ─────────────────────────────────────────────────────────────
 * 목 모드는 **꺼진 것이 기본**입니다.
 * ─────────────────────────────────────────────────────────────
 * 처음에는 반대였습니다 — 목이 기본이고 REACT_APP_USE_OPS_BRIEFING_MOCK='false'
 * 를 넣어야 실서버를 봤습니다. 그런데 `.env` 는 gitignore 대상이라
 * 저장소에 적어 둘 수가 없고, 배포하는 사람이 한 번만 빠뜨리면
 * **모든 고객에게 가짜 회원 이름과 매출이 그대로 나갑니다.**
 *
 * 잊었을 때 안전한 쪽이 기본이어야 합니다. 그래서 뒤집었습니다.
 * 목을 보려면 둘 중 하나를 명시적으로 켜야 합니다.
 *   · 빌드 시     REACT_APP_USE_OPS_BRIEFING_MOCK=true
 *   · 브라우저에서 localStorage.setItem('USE_OPS_BRIEFING_MOCK', 'true')
 *
 * 화면 코드는 어느 쪽이든 손대지 않습니다.
 */

export const USE_MOCK = resolveUseMock();

/** 목 응답이 즉시 오면 로딩 UI 를 한 번도 못 보고 넘어간다. 실제 지연을 흉내낸다 */
const MOCK_DELAY_MS = 220;

const delay = <T,>(value: T): Promise<T> =>
    new Promise((resolve) => {
        setTimeout(() => resolve(value), MOCK_DELAY_MS);
    });

const headers = (accessToken: string) => ({ authorization: accessToken });

/**
 * 라포 서버는 모든 응답을 { success, data } 로 감쌉니다.
 * 화면은 안쪽만 씁니다 — 여기서 한 번에 벗깁니다.
 * 감싸지 않은 응답이 와도 그대로 통과시켜서, 엔드포인트가 하나씩
 * 열리는 동안 화면이 깨지지 않게 합니다.
 */
const unwrap = <T,>(payload: any): T =>
    payload && typeof payload === 'object' && 'data' in payload && 'success' in payload
        ? payload.data
        : payload;

const base = (centerId: string) =>
    `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/ops-briefing`;

type Ctx = { accessToken: string; centerId: string };

/* ═══════════════════════════════════════════════════════════
   ⓪ 노출 여부

   나머지 엔드포인트는 전부 노출 게이트 안이라 닫힌 센터에서 부르면 404 다.
   화면은 무엇을 부르기 전에 여기부터 물어본다.
   ═══════════════════════════════════════════════════════════ */

/**
 * 목 모드에서는 **열린 것으로** 답합니다.
 *
 * 목 모드는 "서버가 없어도 화면을 만들 수 있게" 하려고 있는 장치인데,
 * 여기서 닫힌다고 답하면 라우트 가드가 화면 진입 자체를 막아버려서
 * 목 데이터를 볼 방법이 사라집니다. 목을 켠 사람은 이미 브리핑을 보려는 사람입니다.
 *
 * 고객에게 새어나갈 걱정은 없습니다 — 목 모드는 배포 빌드에서 꺼진 것이 기본이고
 * (opsBriefingMockMode.ts), 켜려면 환경변수나 localStorage 로 명시해야 합니다.
 * 그리고 목이 켜져 있으면 서버를 아예 부르지 않으므로 실데이터가 나올 수도 없습니다.
 */
export const getOpsBriefingAccessAPI = async ({
    accessToken,
    centerId,
}: Ctx): Promise<OpsBriefingAccessResponse> => {
    if (USE_MOCK) return delay({ enabled: true });
    const res = await axios.get(`${base(centerId)}/access`, {
        headers: headers(accessToken),
    });
    return unwrap<OpsBriefingAccessResponse>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ① 오늘의 브리핑
   ═══════════════════════════════════════════════════════════ */

export const getOpsBriefingAPI = async (
    { accessToken, centerId }: Ctx,
    date: DateKst
): Promise<OpsBriefingResponse> => {
    if (USE_MOCK) return delay(mockOpsBriefing(centerId, date));
    const res = await axios.get(`${base(centerId)}`, {
        params: { date },
        headers: headers(accessToken),
    });
    return unwrap<OpsBriefingResponse>(res.data);
};

/**
 * 브리핑 항목 가리기 / 되돌리기.
 *
 * `PATCH /v1/centers/:centerId/ops-briefing/items/:itemId?date=YYYY-MM-DD`
 * body `{ status }`
 *
 * ─────────────────────────────────────────────────────────────
 * ⚠️ `date` 는 **필수**입니다. 그래서 인자로도 필수입니다.
 * ─────────────────────────────────────────────────────────────
 * `itemId` 는 하루 안에서만 고유합니다. 서버가 날짜에 기본값(오늘)을 두면
 * 지난 리포트를 열어 놓았거나 자정을 넘긴 탭에서 **엉뚱한 날짜의 항목**이
 * 가려집니다. 그래서 서버가 기본값을 두지 않고 400 으로 막습니다.
 *
 * ⚠️ 여기서 `dayjs().format(...)` 같은 것으로 오늘을 만들어 넣지 마세요.
 *    호출부(useSetOpsBriefingItemStatusMutation)가 **브리핑을 조회할 때 쓴
 *    그 날짜**를 그대로 넘깁니다. 화면이 보고 있는 날짜와 고치는 날짜가
 *    갈리면 이 인자를 만든 이유가 사라집니다.
 *
 * 응답
 *   200 `{ briefingDate, itemId, status, changed }`
 *   400 date 없음·형식 오류
 *   404 없는 항목 · 그 날짜에 브리핑 없음 · 노출 게이트 닫힘
 *       (**서버에 아직 이 엔드포인트가 없을 때도 404**)
 *   406 이 센터의 소유자가 아님
 *   409 이미 지시가 나간 항목(ACTED) — 가릴 수 없다
 *
 * ⚠️ 서버 배포 전에는 404 가 옵니다. 화면은 낙관 반영을 되돌리고
 *    실패를 그대로 말합니다 — "가렸습니다" 라고 해 놓고 새로고침에 되돌아오는 것이
 *    가장 나쁩니다 (예전 「넘어가기」가 정확히 그랬습니다).
 */
export const setOpsBriefingItemStatusAPI = async (
    { accessToken, centerId }: Ctx,
    date: DateKst,
    itemId: string,
    status: UpdateBriefingItemStatusRequest['status']
): Promise<UpdateBriefingItemStatusResponse> => {
    if (USE_MOCK) return delay(mockSetBriefingItemStatus(date, itemId, status));
    const res = await axios.patch(
        `${base(centerId)}/items/${itemId}`,
        { status },
        { params: { date }, headers: headers(accessToken) }
    );
    return unwrap<UpdateBriefingItemStatusResponse>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ② 원천 데이터
   ═══════════════════════════════════════════════════════════ */

export const getOpsEvidenceAPI = async (
    { accessToken, centerId }: Ctx,
    itemId: string
): Promise<EvidenceResponse | null> => {
    if (USE_MOCK) return delay(mockEvidence(itemId));
    const res = await axios.get(`${base(centerId)}/items/${itemId}/evidence`, {
        headers: headers(accessToken),
    });
    return unwrap<EvidenceResponse | null>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ③ 회원 현황
   ═══════════════════════════════════════════════════════════ */

export const getOpsMembersAPI = async (
    { accessToken, centerId }: Ctx,
    date: DateKst
): Promise<MembersResponse> => {
    if (USE_MOCK) return delay(mockMembers(date));
    const res = await axios.get(`${base(centerId)}/members`, {
        params: { date },
        headers: headers(accessToken),
    });
    return unwrap<MembersResponse>(res.data);
};

/**
 * 회원 가리기 / 다시 보기.
 *
 * `PATCH /v1/centers/:centerId/ops-briefing/members/:memberId/visibility`
 * body `{ hidden }`
 *
 * ─────────────────────────────────────────────────────────────
 * ⚠️ `date` 를 보내지 않습니다. **일부러 없습니다.**
 * ─────────────────────────────────────────────────────────────
 * 항목 가리기(setOpsBriefingItemStatusAPI)는 `?date=` 가 필수인데 이건 반대입니다.
 * 회원 가리기는 (centerId, memberId) 한 쌍이고 **다시 보기 전까지 영구**라
 * 날짜라는 축 자체가 없습니다. 위쪽 함수를 복사해 오면서 `params: { date }` 를
 * 딸려 보내지 마세요 — 서버가 그걸 받기 시작하면 "그날만 가림" 이라는
 * 없는 의미가 생기고, 한 번 나가면 되돌릴 수 없습니다.
 *
 * 응답
 *   200 `{ memberId, hidden, changed }` — changed:false 는 이미 그 상태였다는 뜻(실패 아님)
 *   400 이 센터의 회원이 아님 (가릴 때만 검사)
 *   404 노출 게이트 닫힘 · **서버에 아직 이 엔드포인트가 없을 때도 404**
 *   406 이 센터의 소유자가 아님
 *
 * ⚠️ 서버 배포 전에는 404 가 옵니다. 화면은 애초에 이 버튼을 그리지 않지만
 *    (회원 카드에 `hidden` 키가 없으면 UI 를 숨깁니다), 그래도 실패하면
 *    낙관 반영을 되돌리고 실패를 그대로 말합니다 — "가렸습니다" 라고 해 놓고
 *    새로고침에 되돌아오는 것이 가장 나쁩니다.
 */
export const setOpsMemberHiddenAPI = async (
    { accessToken, centerId }: Ctx,
    memberId: string,
    hidden: boolean
): Promise<UpdateMemberVisibilityResponse> => {
    if (USE_MOCK) return delay(mockSetMemberHidden(memberId, hidden));
    const res = await axios.patch(
        `${base(centerId)}/members/${memberId}/visibility`,
        { hidden },
        { headers: headers(accessToken) }
    );
    return unwrap<UpdateMemberVisibilityResponse>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ④ 강사 현황
   ═══════════════════════════════════════════════════════════ */

export const getOpsCoachesAPI = async (
    { accessToken, centerId }: Ctx,
    month: string
): Promise<CoachesResponse> => {
    if (USE_MOCK) return delay(mockCoaches(month));
    const res = await axios.get(`${base(centerId)}/coaches`, {
        params: { month },
        headers: headers(accessToken),
    });
    return unwrap<CoachesResponse>(res.data);
};

/**
 * 강사 가리기 / 다시 보기.
 *
 * `PATCH /v1/centers/:centerId/ops-briefing/coaches/:coachId/visibility`
 * body `{ hidden }`
 *
 * ─────────────────────────────────────────────────────────────
 * ⚠️ `month` 를 보내지 않습니다. 회원 가리기와 **같은 이유**로 없습니다.
 * ─────────────────────────────────────────────────────────────
 * 강사 현황은 월 단위 조회지만 가리기는 (centerId, coachId) 한 쌍이고
 * **다시 보기 전까지 영구**입니다. 위쪽 `getOpsCoachesAPI` 를 복사해 오면서
 * `params: { month }` 를 딸려 보내지 마세요 — 서버가 그걸 받기 시작하면
 * "그 달만 가림" 이라는 없는 의미가 생기고, 한 번 나가면 되돌릴 수 없습니다.
 *
 * 응답
 *   200 `{ coachId, hidden, changed }` — changed:false 는 이미 그 상태였다는 뜻(실패 아님)
 *   400 이 센터에 연결된 적 없는 강사 (가릴 때만 검사)
 *   404 노출 게이트 닫힘 · **서버에 아직 이 엔드포인트가 없을 때도 404**
 *   406 이 센터의 소유자가 아님
 *
 * ⚠️ 서버 배포 전에는 404 가 옵니다. 화면은 애초에 이 버튼을 그리지 않지만
 *    (강사 행에 `hidden` 키가 없으면 UI 를 숨깁니다), 그래도 실패하면
 *    낙관 반영을 되돌리고 실패를 그대로 말합니다.
 */
export const setOpsCoachHiddenAPI = async (
    { accessToken, centerId }: Ctx,
    coachId: string,
    hidden: boolean
): Promise<UpdateCoachVisibilityResponse> => {
    if (USE_MOCK) return delay(mockSetCoachHidden(coachId, hidden));
    const res = await axios.patch(
        `${base(centerId)}/coaches/${coachId}/visibility`,
        { hidden },
        { headers: headers(accessToken) }
    );
    return unwrap<UpdateCoachVisibilityResponse>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ⑤ 지시 및 진행 현황
   ═══════════════════════════════════════════════════════════ */

/**
 * 지시 목록.
 *
 * ─────────────────────────────────────────────────────────────
 * includeArchived 는 이름보다 넓습니다 — "정리된 건 전부"입니다
 * ─────────────────────────────────────────────────────────────
 * 기본 목록은 `ARCHIVED`(확인 완료)와 `RETRACTED`(철회)를 **둘 다** 뺍니다
 * (서버 opsBriefing.service.listOrders). 그래서 철회한 건을 원장에게 보여주려면
 * 이 옵션이 필요합니다.
 *
 * ⚠️ 그렇다고 진행 중 목록까지 이걸로 부르면 안 됩니다. 서버 상한이 sentAt
 *    내림차순 200건이라, 몇 달치 정리된 건이 그 창을 채우면 **아직 처리 안 된
 *    오래된 지시가 조용히 밀려납니다.** 그래서 화면은 두 번 부릅니다 —
 *    진행 중 목록은 기본으로, 철회 목록만 이 옵션으로.
 */
export const getOpsOrdersAPI = async (
    { accessToken, centerId }: Ctx,
    options: { includeArchived?: boolean; onlyRetracted?: boolean; month?: string } = {}
): Promise<OrderDto[]> => {
    if (USE_MOCK) return delay(mockOrderList(options));
    /*
     * onlyRetracted 는 includeArchived 의 좁은 버전이 아니라 **정렬이 다릅니다.**
     * 서버가 이 옵션에서만 retractedAt 내림차순으로 줍니다 — 오래된 지시를 오늘
     * 철회하면 sentAt 으로는 200건 창 밖으로 밀리고, 그게 전달 실패면 원장이
     * 강사가 철회를 모른다는 사실을 영영 못 봅니다.
     */
    /*
     * 🆕 `month` — 그 달에 나간 지시만 (백엔드 f78c887c, 2026-08-31).
     *
     * 강사 리포트의 「지시 이행률」이 틀려 있었습니다. 리포트는 `ARCHIVED`(정리됨)를
     * **완료로 세는데**(`reportComposer.isDone`) 기본 목록이 그걸 **빼고** 줍니다.
     * 그래서 처리한 지시가 분자·분모 양쪽에서 사라져 월 이행률이 실제보다 낮게
     * 나왔습니다. 리포트는 `includeArchived: true` 와 `month` 를 함께 씁니다.
     *
     * ⚠️ 상한 200건은 **필터 뒤에도 남습니다.** 한 달 지시가 200건을 넘으면
     *    오래된 건이 잘립니다 — 화면이 캡션으로 밝힙니다(`OrderRecord.capped`).
     */
    const params = options.onlyRetracted
        ? { onlyRetracted: true }
        : options.includeArchived || options.month
        ? {
              ...(options.includeArchived ? { includeArchived: true } : {}),
              ...(options.month ? { month: options.month } : {}),
          }
        : undefined;
    const res = await axios.get(`${base(centerId)}/orders`, {
        headers: headers(accessToken),
        ...(params ? { params } : {}),
    });
    const body = unwrap<{ orders: OrderDto[] } | OrderDto[]>(res.data);
    return Array.isArray(body) ? body : body.orders;
};

export const createOpsOrderAPI = async (
    { accessToken, centerId }: Ctx,
    body: CreateOrderRequest
): Promise<OrderDto> => {
    if (USE_MOCK) return delay(mockCreateOrder(body));
    const res = await axios.post(`${base(centerId)}/orders`, body, {
        headers: headers(accessToken),
    });
    return unwrap<OrderDto>(res.data);
};

export const updateOpsOrderAPI = async (
    { accessToken, centerId }: Ctx,
    orderId: string,
    body: UpdateOrderRequest
): Promise<OrderDto> => {
    if (USE_MOCK) return delay(mockUpdateOrder(orderId, body));
    const res = await axios.patch(`${base(centerId)}/orders/${orderId}`, body, {
        headers: headers(accessToken),
    });
    return unwrap<OrderDto>(res.data);
};

/**
 * 지시 철회 — "이건 잘못 보냈습니다".
 *
 * ⚠️ **PATCH 로 `status: 'RETRACTED'` 를 보내면 400 입니다.** 그 PATCH 는 상태만
 *    바꾸는 일반 setter 라 회수 판정·사유 판정·강사 통보가 하나도 실행되지 않습니다.
 *    타입에서도 막아 뒀습니다(UpdateOrderStatus).
 *
 * 응답
 *   200 철회됨. body 는 철회된 뒤의 지시 — `retractDelivery` 로 전달 여부를 봅니다
 *   400 강사가 이미 확인한 지시인데 사유가 없다 → 사유를 받아 다시 부릅니다
 *   404 없는 지시 / 남의 센터
 *   409 이미 철회됨
 */
export const retractOpsOrderAPI = async (
    { accessToken, centerId }: Ctx,
    orderId: string,
    body: RetractOrderRequest
): Promise<OrderDto> => {
    if (USE_MOCK) return delay(mockRetractOrder(orderId, body.reason));
    const res = await axios.post(`${base(centerId)}/orders/${orderId}/retract`, body, {
        headers: headers(accessToken),
    });
    return unwrap<OrderDto>(res.data);
};

/* ═══════════════════════════════════════════════════════════
   ⑥ 지난 리포트
   ═══════════════════════════════════════════════════════════ */

export const getOpsHistoryAPI = async (
    { accessToken, centerId }: Ctx,
    month: string
): Promise<HistoryResponse> => {
    if (USE_MOCK) return delay(mockHistory(month));
    const res = await axios.get(`${base(centerId)}/history`, {
        params: { month },
        headers: headers(accessToken),
    });
    return unwrap<HistoryResponse>(res.data);
};

/** 지난 브리핑 항목을 뒤늦게 실행 처리 */
export const setHistoryItemDoneAPI = async (
    { accessToken, centerId }: Ctx,
    date: DateKst,
    index: number,
    done: boolean
): Promise<void> => {
    if (USE_MOCK) {
        mockSetHistoryItemDone(date, index, done);
        await delay(null);
        return;
    }
    await axios.patch(
        `${base(centerId)}/history/${date}/items/${index}`,
        { done },
        { headers: headers(accessToken) }
    );
};

/* ═══════════════════════════════════════════════════════════
   ⑦ 세팅 집계 2종 (백엔드 3차 B5·B8)

   🔴 **둘 다 실패를 조용히 삼킵니다.** 아래 `optional()` 주석 참고.
   ═══════════════════════════════════════════════════════════ */

/**
 * 있으면 쓰고, 없으면 **없는 대로 둔다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 에러를 던지지 않나 — 여기만 예외입니다
 * ─────────────────────────────────────────────────────────────
 * 이 두 엔드포인트는 **백엔드보다 웹이 먼저 배포될 수 있습니다.** 그러면 서버에
 * 라우트가 없어 404 가 오는데, 그때 React Query 가 `isError` 로 넘어가면
 * 화면이 「불러오지 못했습니다」를 그립니다 — 원장님 입장에서는 **멀쩡하던
 * 브리핑이 고장 난 것**입니다. 있지도 않던 카드 때문에요.
 *
 * 이 두 카드는 **부가 정보**입니다. 없으면 없는 대로 기존 화면이 그대로 돌아야
 * 합니다. 그래서 실패를 `null` 로 바꿔 「이 서버에는 아직 없다」로 다룹니다.
 *
 * ⚠️ 다른 엔드포인트에는 이 패턴을 쓰지 마세요. 회원 현황·브리핑 본문이 조용히
 *    비면 원장님은 「오늘은 챙길 일이 없구나」로 읽습니다 — 그건 거짓말입니다.
 *    실패를 삼켜도 되는 것은 **없어도 화면이 성립하는 카드**뿐입니다.
 *
 * ⚠️ 404 만 삼키지 않고 전부 삼킵니다. 500·네트워크 단절도 마찬가지로 카드를
 *    안 그릴 뿐이고, 그게 이 카드에서는 맞는 동작입니다. 대신 콘솔에는 남깁니다 —
 *    조용히 사라지는 것과 조용히 삼킨 것을 개발자는 구분할 수 있어야 합니다.
 */
const optional = async <T,>(label: string, run: () => Promise<T>): Promise<T | null> => {
    try {
        return await run();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.info(`[opsBriefing] ${label} 없음 — 카드를 그리지 않습니다`, e);
        return null;
    }
};

/** 신규 센터 세팅 졸업 현황 (B5). 서버에 없으면 null */
export const getOpsOnboardingAPI = async ({
    accessToken,
    centerId,
}: Ctx): Promise<OnboardingPayload | null> => {
    if (USE_MOCK) return delay(mockOnboarding());
    return optional('onboarding', async () => {
        const res = await axios.get(`${base(centerId)}/onboarding`, {
            headers: headers(accessToken),
        });
        return unwrap<OnboardingPayload>(res.data);
    });
};

/**
 * 강사별 온보딩 집계 (2026-09-01 신규·과도기 화면).
 *
 * 🔴 **백엔드가 병렬 구현 중이라 지금은 대부분 null 이 돌아온다.**
 *    `optional` 이 404 를 삼키므로 화면은 기존 축 카드로 조용히 떨어진다.
 *    이 폴백이 곧 배포 안전장치다 — 서버가 먼저 나가든 나중에 나가든
 *    화면이 비지 않는다.
 */
export const getOpsCoachOnboardingAPI = async ({
    accessToken,
    centerId,
}: Ctx): Promise<CoachOnboardingBriefing | null> => {
    if (USE_MOCK) return delay(FIXTURE_BRIEFING);
    return optional('coach-onboarding', async () => {
        const res = await axios.get(`${base(centerId)}/coach-onboarding`, {
            headers: headers(accessToken),
        });
        return unwrap<CoachOnboardingBriefing>(res.data);
    });
};

/**
 * 카드 처리·가리기 이력 저장 (정책 §4-3).
 *
 * ⚠️ **실패해도 화면은 계속 간다.** 서버에 이 API 가 아직 없을 수 있고,
 *    그때는 부르는 쪽이 localStorage 폴백으로 같은 일을 한다.
 *    성공 여부를 돌려주는 이유가 그것이다 — 삼키지 말고 알려야
 *    폴백으로 갈지 말지를 부르는 쪽이 정한다.
 *
 * 서버 라우트는 `POST /v1/centers/:centerId/ops-briefing/coach-onboarding/:coachId/action`
 * (`rappo/src/routes/v1/opsBriefing.route.ts`)이고 body 는 `{ type }` 뿐이다 — 검증(Joi)이
 * 모르는 키를 거부하므로 coachId 를 body 에 같이 실으면 400 이다.
 *
 * 🔴 2026-09-10 까지 `…/ops-briefing/briefing-action` 으로 보내고 있었다. 서버에 없는 주소라
 *    항상 404 → 로컬 폴백으로 떨어졌고, 서버가 `actions` 를 주는 센터에서는 그 로컬 기록을
 *    읽지도 않아 체크가 토스트만 남기고 사라졌다. 주소는 `opsBriefingActionRequest.test.ts`
 *    가 지킨다 — 화면 테스트는 이 함수를 목으로 갈아 끼워서 주소를 못 본다.
 */
export const postOpsBriefingActionAPI = async (
    { accessToken, centerId }: Ctx,
    { coachId, type }: { coachId: string; type: 'CHECK' | 'HIDE_TODAY' }
): Promise<boolean> => {
    if (USE_MOCK) {
        await delay(null);
        return true;
    }
    try {
        await axios.post(
            `${base(centerId)}/coach-onboarding/${encodeURIComponent(coachId)}/action`,
            { type },
            { headers: headers(accessToken) }
        );
        return true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.info('[opsBriefing] 온보딩 처리 표시를 서버에 저장하지 못함 — 이 브라우저에만 남깁니다', e);
        return false;
    }
};

/** 강사 정착 8단계 원본 + 해금 컨텍스트 (B8·B9). 서버에 없으면 null */
export const getOpsCoachSettlingAPI = async ({
    accessToken,
    centerId,
}: Ctx): Promise<CoachSettlingPayload | null> => {
    if (USE_MOCK) {
        return delay({
            coaches: mockCoachSettling(),
            reregisteredMembers: null,
            totalMembers: null,
            unlock: mockUnlockContext(),
        });
    }
    return optional('coach-settling', async () => {
        const res = await axios.get(`${base(centerId)}/coach-settling`, {
            headers: headers(accessToken),
        });
        return unwrap<CoachSettlingPayload>(res.data);
    });
};

/** 화면에서 목 모드 여부를 알아야 할 때 (개발 배지 등) */
export const isOpsBriefingMock = () => USE_MOCK;
